/**
 * FORMULARIO CTA SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con secciones anteriores:
 * - Fondo: #ffffff
 * - Colores: #0b1220, #6b7280, #0b63c6
 * - Form inputs con border 1px
 * - Sin gradientes decorativos
 * - Animaciones reveal con Intersection Observer
 * - Success state minimalista
 */

import React, { useState, useRef, useEffect } from 'react';
import API_URL from '../../config/api';
import { 
  Send, 
  CheckCircle2,
  User,
  Building2,
  Mail,
  Phone,
  Truck,
  MessageCircle,
  Calendar,
  Shield,
  Clock,
  Award,
  Quote,
  Sparkles
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FormularioCTA = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    vehiculos: '',
    canalPreferido: 'whatsapp',
    comentarios: ''
  });

  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const headerRef = useReveal(0);
  const beneficiosRef = useReveal(0);
  const formRef = useReveal(100);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);

    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Lead creado exitosamente:', data);
        setEnviado(true);
        
        setTimeout(() => {
          document.getElementById('mensaje-exito')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        alert(data.message || 'Hubo un error al enviar el formulario. Por favor intenta nuevamente.');
        setEnviando(false);
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      alert('Error de conexión. Por favor verifica tu internet e intenta nuevamente.');
      setEnviando(false);
    }
  };

  const beneficios = [
    {
      titulo: "Análisis de tu situación actual",
      descripcion: "Conversamos sobre tu operación, número de vehículos, tipo de carga y desafíos específicos."
    },
    {
      titulo: "Demo del sistema en vivo",
      descripcion: "Te mostramos exactamente cómo funciona: alertas, reportes y control de rentabilidad en acción."
    },
    {
      titulo: "Estimación de recuperación",
      descripcion: "Calculamos aproximadamente cuánto podrías recuperar mensualmente al controlar tus costos."
    },
    {
      titulo: "Propuesta personalizada",
      descripcion: "Si tiene sentido para ti, recibes propuesta con alcance, inversión y próximos pasos (sin presión)."
    }
  ];

  const garantias = [
    { icon: <Shield />, texto: "Sin compromiso de compra" },
    { icon: <Clock />, texto: "Respuesta en menos de 24h" },
    { icon: <Calendar />, texto: "Diagnóstico 100% gratuito" },
    { icon: <Award />, texto: "Personalizado a tu caso" }
  ];

  return (
    <section
      id="formulario-contacto"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {!enviado ? (
          <>
            {/* ── Header ── */}
            <header ref={headerRef} className="max-w-[720px] mx-auto flex flex-col gap-4 text-center">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mx-auto">
                <Calendar size={14} strokeWidth={2} className="text-emerald-600" />
                <span className="text-[13px] font-medium text-emerald-700">Último paso</span>
              </div>

              <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
                Solicita tu diagnóstico{' '}
                <span className="text-emerald-600">gratuito ahora</span>
              </h2>

              <p className="text-[15px] text-[#6b7280] leading-[1.6]">
                <strong className="text-emerald-600 font-semibold">15 minutos</strong>{' '}
                pueden cambiar la rentabilidad de tu empresa. Completa el formulario y te contactamos en menos de 24 horas.
              </p>
            </header>

            {/* ── Grid 2 columnas ── */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8">

              {/* COLUMNA IZQUIERDA - Beneficios */}
              <div ref={beneficiosRef} className="flex flex-col gap-6">
                <h3 className="text-[18px] font-bold text-[#0b1220]">
                  ¿Qué obtienes con el diagnóstico?
                </h3>

                {/* Beneficios */}
                <div className="flex flex-col gap-3">
                  {beneficios.map((item, i) => (
                    <div
                      key={i}
                      className="flex gap-3 p-4 bg-white rounded-lg border border-black/[0.08] hover:border-emerald-500/40 hover:shadow-[0_8px_20px_rgba(15,23,42,0.06)] transition-all duration-300"
                    >
                      <div className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 size={16} strokeWidth={2} className="text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="text-[14px] font-semibold text-[#0b1220] leading-tight mb-1">
                          {item.titulo}
                        </h4>
                        <p className="text-[13px] text-[#6b7280] leading-[1.6]">
                          {item.descripcion}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Garantías */}
                <div className="grid grid-cols-2 gap-2 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  {garantias.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-white flex items-center justify-center shrink-0">
                        {React.cloneElement(item.icon, {
                          size: 14,
                          strokeWidth: 2,
                          className: 'text-[#0b63c6]'
                        })}
                      </div>
                      <span className="text-[12.5px] font-medium text-[#0b1220]">
                        {item.texto}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Testimonial */}
                <div className="relative flex flex-col gap-2 p-5 bg-emerald-50 rounded-lg border-l-2 border-emerald-500">
                  <div className="absolute top-3 left-3 opacity-10">
                    <Quote size={32} strokeWidth={2} className="text-emerald-600" />
                  </div>
                  <p className="text-[13.5px] text-[#0b1220] italic leading-[1.65] relative z-10">
                    "El diagnóstico fue súper claro. Me mostraron el sistema funcionando y me dieron números reales de cuánto podría mejorar. Contraté el mismo día."
                  </p>
                  <div className="text-[12px] font-semibold text-emerald-700 relative z-10">
                    — Carlos M., Transportes del Sur
                  </div>
                </div>
              </div>

              {/* COLUMNA DERECHA - Formulario */}
              <div
                ref={formRef}
                className="bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.05)] overflow-hidden"
              >
                {/* Header form */}
                <div className="p-6 bg-gradient-to-r from-gray-50 to-white border-b border-black/[0.06]">
                  <h3 className="text-[18px] font-bold text-[#0b1220] mb-1">
                    Completa tus datos
                  </h3>
                  <p className="text-[13.5px] text-[#6b7280]">
                    Solo información básica. Te contactamos en menos de 24h.
                  </p>
                </div>

                {/* Form body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                  {/* Nombre */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      <User size={16} strokeWidth={2} className="text-[#0b63c6]" />
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Ej: Juan Pérez Rodríguez"
                      required
                      className="w-full px-4 py-2.5 text-[14px] border border-black/[0.12] rounded-lg focus:outline-none focus:border-[#0b63c6] focus:ring-2 focus:ring-[#0b63c6]/20 transition-all"
                    />
                  </div>

                  {/* Empresa */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      <Building2 size={16} strokeWidth={2} className="text-[#0b63c6]" />
                      Nombre de tu empresa *
                    </label>
                    <input
                      type="text"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      placeholder="Ej: Transportes del Norte SAC"
                      required
                      className="w-full px-4 py-2.5 text-[14px] border border-black/[0.12] rounded-lg focus:outline-none focus:border-[#0b63c6] focus:ring-2 focus:ring-[#0b63c6]/20 transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      <Mail size={16} strokeWidth={2} className="text-[#0b63c6]" />
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Ej: juan.perez@empresa.com"
                      required
                      className="w-full px-4 py-2.5 text-[14px] border border-black/[0.12] rounded-lg focus:outline-none focus:border-[#0b63c6] focus:ring-2 focus:ring-[#0b63c6]/20 transition-all"
                    />
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      <Phone size={16} strokeWidth={2} className="text-[#0b63c6]" />
                      Teléfono / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="Ej: 987 654 321"
                      required
                      className="w-full px-4 py-2.5 text-[14px] border border-black/[0.12] rounded-lg focus:outline-none focus:border-[#0b63c6] focus:ring-2 focus:ring-[#0b63c6]/20 transition-all"
                    />
                  </div>

                  {/* Vehículos */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      <Truck size={16} strokeWidth={2} className="text-[#0b63c6]" />
                      ¿Cuántos vehículos tienes? *
                    </label>
                    <select
                      name="vehiculos"
                      value={formData.vehiculos}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 text-[14px] border border-black/[0.12] rounded-lg focus:outline-none focus:border-[#0b63c6] focus:ring-2 focus:ring-[#0b63c6]/20 transition-all appearance-none bg-white cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="">Selecciona una opción</option>
                      <option value="2-3">2-3 vehículos</option>
                      <option value="4-6">4-6 vehículos</option>
                      <option value="7-10">7-10 vehículos</option>
                      <option value="11-15">11-15 vehículos</option>
                      <option value="mas-15">Más de 15 vehículos</option>
                    </select>
                  </div>

                  {/* Canal preferido */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      <MessageCircle size={16} strokeWidth={2} className="text-[#0b63c6]" />
                      ¿Cómo prefieres que te contactemos? *
                    </label>
                    <div className="flex flex-col gap-2">
                      {[
                        { value: 'whatsapp', icon: <MessageCircle />, label: 'WhatsApp' },
                        { value: 'llamada', icon: <Phone />, label: 'Llamada telefónica' },
                        { value: 'email', icon: <Mail />, label: 'Email' }
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`
                            flex items-center gap-3 px-4 py-2.5 rounded-lg border cursor-pointer transition-all
                            ${formData.canalPreferido === option.value
                              ? 'border-[#0b63c6] bg-blue-50'
                              : 'border-black/[0.12] hover:border-black/[0.2] hover:bg-gray-50'
                            }
                          `}
                        >
                          <input
                            type="radio"
                            name="canalPreferido"
                            value={option.value}
                            checked={formData.canalPreferido === option.value}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#0b63c6] cursor-pointer"
                          />
                          <div className="flex items-center gap-2 flex-1">
                            {React.cloneElement(option.icon, {
                              size: 16,
                              strokeWidth: 2,
                              className: formData.canalPreferido === option.value ? 'text-[#0b63c6]' : 'text-[#6b7280]'
                            })}
                            <span className={`text-[14px] font-medium ${
                              formData.canalPreferido === option.value ? 'text-[#0b63c6]' : 'text-[#6b7280]'
                            }`}>
                              {option.label}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Comentarios */}
                  <div>
                    <label className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0b1220] mb-2">
                      Comentarios adicionales (opcional)
                    </label>
                    <textarea
                      name="comentarios"
                      value={formData.comentarios}
                      onChange={handleChange}
                      placeholder="¿Algo específico que quieras comentarnos?"
                      rows="3"
                      className="w-full px-4 py-2.5 text-[14px] border border-black/[0.12] rounded-lg focus:outline-none focus:border-[#0b63c6] focus:ring-2 focus:ring-[#0b63c6]/20 transition-all resize-none"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={enviando}
                    className="w-full flex items-center justify-center gap-2 bg-[#0b63c6] hover:bg-[#0957b0] disabled:bg-gray-400 text-white text-[15px] font-semibold px-6 py-3.5 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                  >
                    {enviando ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send size={18} strokeWidth={2} />
                        Solicitar Diagnóstico Gratuito
                      </>
                    )}
                  </button>

                  <p className="text-[12px] text-[#6b7280] text-center leading-[1.6]">
                    Al enviar este formulario aceptas que te contactemos para agendar tu diagnóstico gratuito. No compartimos tu información con terceros.
                  </p>
                </form>
              </div>
            </div>
          </>
        ) : (
          /* ── Mensaje de Éxito ── */
          <div
            id="mensaje-exito"
            className="max-w-[700px] mx-auto flex flex-col items-center gap-6 p-8 bg-white rounded-xl border border-emerald-200 shadow-[0_10px_30px_rgba(16,185,129,0.12)]"
          >
            {/* Icon */}
            <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
              <CheckCircle2 size={40} strokeWidth={2} className="text-white" />
            </div>

            {/* Content */}
            <div className="text-center">
              <h2 className="text-[24px] font-bold text-[#0b1220] mb-2">
                ¡Solicitud recibida con éxito!
              </h2>
              <p className="text-[15px] text-[#6b7280]">
                Gracias <strong className="text-emerald-600">{formData.nombre}</strong>. Hemos recibido tu solicitud de diagnóstico gratuito.
              </p>
            </div>

            {/* Pasos */}
            <div className="w-full">
              <h3 className="text-[16px] font-bold text-[#0b1220] mb-4 text-center">
                ¿Qué sigue ahora?
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  `En las próximas 24 horas te contactamos por ${formData.canalPreferido === 'whatsapp' ? 'WhatsApp' : formData.canalPreferido === 'llamada' ? 'teléfono' : 'email'} al ${formData.telefono}`,
                  'Agendamos tu videollamada en el horario que mejor te convenga',
                  'Realizamos el diagnóstico de 15 minutos donde vemos si el sistema es adecuado para ti'
                ].map((texto, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                      <span className="text-[14px] font-bold text-white">{i + 1}</span>
                    </div>
                    <p className="text-[14px] text-[#0b1220] leading-[1.6]">
                      {texto}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Nota */}
            <div className="w-full flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Shield size={18} strokeWidth={2} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[13px] text-[#0b1220] leading-[1.6]">
                Si no recibes nuestro contacto en 24 horas, revisa tu carpeta de spam o escríbenos directamente por WhatsApp.
              </p>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default FormularioCTA;